// src/components/Control/Operator/SmartHome.tsx
// Дворецкий «Грач» и умный дом (ноды audio и home на роботе). Раздел настроек «Умный дом и Грач».
//   robot/<id>/audio/cmd  <- {"cmd":"butler","butler","butler_names","butler_confirm"}; что слышит — audio/state.butler
//   robot/<id>/home/cmd   <- {"cmd":"light","targets":[id],"on"|"level"|"color"|"rgb"|"effect"|"preset_id"}
//                            | {"cmd":"config","devices":[{id,host,name,aliases}],"transition_s"}
//                            | {"cmd":"identify","id"} (мигнуть) | {"cmd":"refresh"}
//                            | {"cmd":"scene_save"|"scene_apply"|"scene_delete","name"|"id"} — пресеты дома
//                            | {"cmd":"schedule_set","id"?,"time","scene"|"off","days","enabled"} | {"cmd":"schedule_delete","id"}
//   robot/<id>/home/state -> HomeState (telemetry), home/event -> история событий (events.ts)
// Голосом лентами управляет сам робот (voice_commands.py + home.py), сайт — то же самое кнопками.
import React, { useEffect, useRef, useState } from 'react';
import { useRobot, useTelemetryState, useUiActions } from '../robot/RobotContext';
import { HomeDevice, HomeScene, HomeScheduleEntry, STALE, fresh } from '../robot/telemetry';
import { putJson } from '../../../hooks/useZenohSubscription';
import { useTick } from '../../../hooks/useTick';
import { timeOf } from '../../../types/features';
import { useAudioState } from '../Features/NannyPanel';
import { Segmented } from './ui';

// Цвета, которые знает голос (id — как в voice_commands.py; RGB для лент — в home.py, здесь только образец)
const COLORS: [string, string, string][] = [
  ['white', 'белый', '#ffffff'], ['warm', 'тёплый белый', '#ff9a3c'], ['neutral', 'дневной белый', '#ffcd96'],
  ['cold', 'холодный белый', '#bed7ff'], ['red', 'красный', '#ff0000'], ['orange', 'оранжевый', '#ff5000'],
  ['yellow', 'жёлтый', '#ffb400'], ['green', 'зелёный', '#00ff00'], ['turquoise', 'бирюзовый', '#00ffa0'],
  ['cyan', 'голубой', '#00a0ff'], ['blue', 'синий', '#0000ff'], ['purple', 'фиолетовый', '#8200ff'],
  ['pink', 'розовый', '#ff2878'],
];
// Эффекты, которые можно назвать голосом (английское имя WLED -> как сказать)
const VOICE_EFFECTS: Record<string, string> = {
  Solid: 'без эффектов', Rainbow: 'радуга', Colorloop: 'переливы', 'Fire 2012': 'огонь', Candle: 'свеча',
  Breathe: 'дыхание', Twinkle: 'мерцание', Fireworks: 'салют', Aurora: 'северное сияние', Pacifica: 'океан',
  Running: 'бегущие огни',
};
const LIGHT_EXAMPLES = ['Грач, свет', 'Грач, включи свет', 'Грач, сохрани свет как вечер', 'Грач, включи вечер',
  'Грач, каждый день в семь утра включай утро', 'Грач, какое расписание', 'Грач, включи белый на полную', 'Грач, сделай потише', 'Грач, ещё',
  'Грач, выключи вторую ленту', 'Грач, яркость на тридцать процентов', 'Грач, включи тёплый', 'Грач, включи радугу',
  'Грач, выключи все ленты'];
const HEARD_KIND: Record<string, string> = {
  command: 'команда', maybe: 'команда (имя послышалось похоже)', armed: 'имя — жду команду', ignored: 'не мне',
};
const ASR_NS = ['выключено', 'слабое', 'среднее (рекомендуется)', 'сильное', 'максимальное'];
const CONFIRM: ['beep' | 'voice' | 'off', string][] = [['beep', 'сигнал'], ['voice', 'голосом'], ['off', 'молча']];

const toHex = (rgb: number[] | null) => rgb ? `#${rgb.map(c => Math.max(0, Math.min(255, c)).toString(16).padStart(2, '0')).join('')}` : '#000000';
const fromHex = (hex: string) => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
const splitNames = (text: string) => text.split(',').map(s => s.trim()).filter(Boolean);
const isSwitch = (d: HomeDevice) => d.kind === 'tuya';
// Устройство, как его хранит робот (что сайт показывает — то и возвращает)
const conf = (x: HomeDevice) => ({
  id: x.id, host: x.host, name: x.name, aliases: x.aliases,
  ...(x.kind ? { kind: x.kind } : {}), ...(x.group ? { group: x.group } : {}),
  ...(isSwitch(x) ? { tuya_id: x.tuya_id, channel: x.channel, version: x.version } : {}),
});

function useHome() {
  useTick(1000);
  const { session, robotId, notify } = useRobot();
  const { st, at } = useTelemetryState(t => ({ st: t.home, at: t.homeAt }));
  const send = (msg: object) => putJson(session, `robot/${robotId}/home/cmd`, msg)
    .then(ok => { if (!ok) notify('Не удалось отправить команду умному дому', 'error'); return ok; });
  return { st: st && fresh(at, STALE.node) ? st : null, send, online: !!session };
}

// ---------- дворецкий ----------

const ButlerCard: React.FC = () => {
  const { session, robotId, voice } = useRobot();
  const { toggleVoice } = useUiActions();
  const { state, online } = useAudioState();
  const n = state?.nanny;
  const [names, setNames] = useState('');
  useEffect(() => { if (n?.butler_names) setNames(n.butler_names.join(', ')); }, [n?.butler_names?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (patch: object) => putJson(session, `robot/${robotId}/audio/cmd`, { cmd: 'butler', ...patch });
  const commitNames = () => {
    const list = splitNames(names);
    if (list.length && list.join(',') !== n?.butler_names?.join(',')) set({ butler_names: list });
  };
  const b = state?.butler;
  const supported = n?.butler !== undefined;
  const badge = !online ? ['off', 'нет связи'] : !supported ? ['off', 'старое ПО на Pi'] : !state!.recognition ? ['alert', 'нет Vosk на роботе']
    : b?.armed ? ['warn', 'ждёт команду'] : b?.on ? ['ok', 'слушает'] : ['off', 'выключен'];
  const run = (text: string) => {
    toggleVoice(true);
    voice.sendText(text);
  };
  return (
    <div className="feature-card">
      <h3><span>Грач — голосовой дворецкий</span><span className={`badge ${badge[0]}`}>{badge[1]}</span></h3>
      {online && supported ? (
        <>
          <label className="op-check">
            <input type="checkbox" checked={!!n!.butler} onChange={e => set({ butler: e.target.checked })} />
            Слушать всегда и откликаться на имя
          </label>
          <div className="op-trainer-grid">
            <label className="op-trainer-field">
              <span>Имя (можно несколько через запятую)</span>
              <input type="text" value={names} onChange={e => setNames(e.target.value)} onBlur={commitNames}
                     onKeyDown={e => e.key === 'Enter' && commitNames()} />
            </label>
            <div className="op-trainer-field">
              <span>Ответ на понятую команду</span>
              <Segmented label="Ответ на понятую команду" value={n!.butler_confirm ?? 'beep'} options={CONFIRM}
                         onChange={v => set({ butler_confirm: v })} />
            </div>
          </div>
          <label className="op-check">
            <input type="checkbox" checked={n!.butler_grammar ?? true} onChange={e => set({ butler_grammar: e.target.checked })} />
            Распознавать только слова команд (намного точнее; «скажи …» — свободно)
          </label>
          <div className="op-trainer-grid">
            <label className="op-trainer-field">
              <span>Шумоподавление для распознавания</span>
              <select value={n!.asr_ns ?? 2} onChange={e => set({ asr_ns: Number(e.target.value) })}>
                {ASR_NS.map((t, i) => <option key={t} value={i}>{t}</option>)}
              </select>
            </label>
            <label className="op-check">
              <input type="checkbox" checked={!!n!.asr_agc} onChange={e => set({ asr_agc: e.target.checked })} />
              Автоусиление для распознавания
            </label>
          </div>
          <label className="op-check">
            <input type="checkbox" checked={!!n!.butler_keep_audio} onChange={e => set({ butler_keep_audio: e.target.checked })} />
            Сохранять звук последних 60 фраз на роботе (папка recordings/butler) — для настройки распознавания
          </label>
          <p className="hint">Сильное шумоподавление искажает речь, и распознавание путается; для прослушивания и записей
            оно задаётся отдельно — в «Голос и звук». Усиление микрофона — общее, там же.</p>
          {b?.model && <p className="hint">Имя слышит Vosk, команду — {b.model === 'GigaAM' ? 'GigaAM (Сбер, на роботе)' : 'Vosk'}.</p>}
          {b?.heard && <p className="hint" role="status">Последнее обращение: «{b.heard.text}» · {timeOf(b.heard.at)}</p>}
          {b?.on && (
            <details className="op-home-names">
              <summary>Что слышит робот ({b.recent?.length ?? 0})</summary>
              {b.recent?.length ? (
                <ul className="event-list">
                  {[...b.recent].reverse().map(r => (
                    <li key={`${r.at}-${r.text}`} className={r.kind === 'ignored' ? '' : 'ok'}>
                      <span className="time">{timeOf(r.at)}</span>
                      <span className="text">«{r.text}»{r.free ? ` (${b?.model === 'GigaAM' ? 'GigaAM' : 'свободно'}: «${r.free}»)` : ''} — {HEARD_KIND[r.kind] ?? r.kind}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="hint">Пока ничего не распознал. Скажите что-нибудь рядом с роботом.</p>}
              <p className="hint">Последние фразы распознавателя текстом (звук не хранится) — чтобы видеть, как робот
                понимает сказанное. Маленькая модель Vosk часто слышит «Грач» как «врач»: это тоже засчитывается.</p>
            </details>
          )}
        </>
      ) : <p className="hint">Нода audio на роботе не отвечает{online ? ' или её ПО ещё без дворецкого' : ''}.</p>}
      <p className="hint">
        <b>Как говорить.</b> Одной фразой — «Грач, включи свет», или сначала «Грач», после короткого сигнала — команду
        (в течение 10 с; просто «включи» или «выключи» — это про свет). Не понял — скажет «Не понял»; лента не ответила — скажет, какая. «Ещё», «потише», «ярче» без
        уточнений относятся к ленте из прошлой команды (полминуты), «потише» после команды не про свет — громкость.
      </p>
      <p className="hint">
        Микрофон робота включён постоянно, и в режиме сна тоже. Речь распознаётся на самом роботе (Vosk), звук не
        записывается и никуда не уходит; на сайт попадают только последние фразы текстом. Пока робот говорит, он не
        слушает, чтобы не откликаться на себя.
      </p>
      <p className="hint">Попробовать текстом: {LIGHT_EXAMPLES.map((e, i) => (
        <React.Fragment key={e}>{i > 0 && ', '}<button type="button" className="btn-icon link" disabled={!session} onClick={() => run(e)}>{e}</button></React.Fragment>
      ))}.</p>
    </div>
  );
};

// ---------- одна лента ----------

const NamesForm: React.FC<{ d: HomeDevice; all: HomeDevice[]; send: (m: object) => void }> = ({ d, all, send }) => {
  const [name, setName] = useState(d.name);
  const [aliases, setAliases] = useState(d.aliases.join(', '));
  const [host, setHost] = useState(d.host);
  useEffect(() => { setName(d.name); setAliases(d.aliases.join(', ')); setHost(d.host); }, [d.name, d.aliases.join(','), d.host]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = () => send({ cmd: 'config', devices: all.map(x => x.id === d.id ? { ...conf(x), name: name.trim(), aliases: splitNames(aliases), host: host.trim() } : conf(x)) });
  const remove = () => {
    if (window.confirm(`Убрать «${d.name || d.id}» из умного дома?`)) send({ cmd: 'config', devices: all.filter(x => x.id !== d.id).map(conf) });
  };
  const changed = name.trim() !== d.name || splitNames(aliases).join(',') !== d.aliases.join(',') || host.trim() !== d.host;
  return (
    <details className="op-home-names">
      <summary>Имена и адрес</summary>
      <div className="op-trainer-grid">
        <label className="op-trainer-field">
          <span>Название (так можно назвать голосом)</span>
          <input type="text" value={name} placeholder={isSwitch(d) ? 'например, основной свет' : 'например, у дивана'} maxLength={40}
                 onChange={e => setName(e.target.value)} />
        </label>
        <label className="op-trainer-field">
          <span>Ещё имена через запятую</span>
          <input type="text" value={aliases} placeholder={isSwitch(d) ? 'люстра, верхний свет' : 'диван, гостиная'}
                 onChange={e => setAliases(e.target.value)} />
        </label>
        <label className="op-trainer-field">
          <span>{isSwitch(d) ? 'IP выключателя' : 'Адрес (имя .local или IP)'}</span>
          <input type="text" value={host} onChange={e => setHost(e.target.value)} />
        </label>
      </div>
      <div className="row">
        <button type="button" className="btn-sm primary" disabled={!changed || !host.trim()} onClick={save}>Сохранить</button>
        <button type="button" className="btn-sm danger" onClick={remove}>Убрать</button>
      </div>
      <p className="hint">{isSwitch(d)
        ? 'Голосом: «свет», «основной свет», «люстра» и просто «включи / выключи» — это он; цвет и яркость — только у лент.'
        : 'Голосом ленту можно звать и по номеру: «первая», «вторая» — в порядке лент этого списка.'}</p>
    </details>
  );
};

const StripCard: React.FC<{ d: HomeDevice; n: number; all: HomeDevice[]; send: (m: object) => void }> = ({ d, n, all, send }) => {
  const [level, setLevel] = useState<number | null>(null);
  const lastColor = useRef(0);
  const light = (patch: object) => send({ cmd: 'light', targets: [d.id], source: 'site', ...patch });
  const shown = level ?? d.level ?? 50;
  const commitLevel = () => {
    if (level != null) light({ level });
    setTimeout(() => setLevel(null), 1500);
  };
  const pickColor = (hex: string) => {
    const now = Date.now();
    if (now - lastColor.current < 250) return; // палитра шлёт цвет на каждое движение
    lastColor.current = now;
    light({ rgb: fromHex(hex) });
  };
  const usable = d.online === true;
  const badge = d.online === null ? ['off', 'проверяю…'] : !d.online ? ['alert', d.error ?? 'не отвечает'] : d.on ? ['ok', `включена · ${d.level ?? '—'} %`] : ['off', 'выключена'];
  return (
    <div className="feature-card">
      <h3>
        <span className="op-home-title">
          <span className="op-home-dot" style={{ background: d.on && d.rgb ? toHex(d.rgb) : 'transparent' }} aria-hidden="true" />
          {n}. {d.name || d.id}
        </span>
        <span className={`badge ${badge[0]}`}>{badge[1]}</span>
      </h3>
      <div className="row">
        <button type="button" className={`op-btn ${d.on ? '' : 'primary'}`} disabled={!usable} onClick={() => light({ on: !d.on })}>
          {d.on ? 'Выключить' : 'Включить'}
        </button>
        <button type="button" className="btn-sm" disabled={!usable} title="Мигнёт белым несколько секунд — видно, какая это лента"
                onClick={() => send({ cmd: 'identify', id: d.id })}>Мигнуть</button>
      </div>
      <label className="slider-row">
        <span>Яркость</span>
        <input type="range" min={1} max={100} value={shown} disabled={!usable} aria-label={`Яркость ленты ${d.name || d.id}`}
               onChange={e => setLevel(Number(e.target.value))} onPointerUp={commitLevel} onKeyUp={commitLevel} />
        <span className="value">{shown}%</span>
      </label>
      <div className="op-home-swatches" role="group" aria-label="Цвет">
        {COLORS.map(([id, name, hex]) => (
          <button key={id} type="button" title={name} aria-label={name} disabled={!usable} style={{ background: hex }}
                  onClick={() => light({ color: id })} />
        ))}
        <label className="op-home-picker" title="Любой цвет">
          <input type="color" value={toHex(d.rgb)} disabled={!usable} aria-label="Любой цвет" onChange={e => pickColor(e.target.value)} />
        </label>
      </div>
      <div className="op-trainer-grid">
        <label className="op-trainer-field">
          <span>Эффект</span>
          <select value={d.effect ?? ''} disabled={!usable || !d.effects.length} onChange={e => light({ effect: e.target.value, on: true })}>
            {d.effect === null && <option value="">—</option>}
            {d.effects.map(e => <option key={e} value={e}>{VOICE_EFFECTS[e] ? `${e} — «${VOICE_EFFECTS[e]}»` : e}</option>)}
          </select>
        </label>
        {d.presets.length > 0 && (
          <div className="op-trainer-field">
            <span>Пресеты WLED (их можно назвать голосом: «включи …»)</span>
            <div className="row">
              {d.presets.map(p => <button key={p.id} type="button" className="btn-sm" disabled={!usable} onClick={() => light({ preset_id: p.id })}>{p.name}</button>)}
            </div>
          </div>
        )}
      </div>
      <p className="hint">{d.host}{d.leds ? ` · ${d.leds} светодиодов` : ''}{d.ver ? ` · WLED ${d.ver}` : ''}{d.seen ? ` · на связи ${timeOf(d.seen)}` : ''}</p>
      <NamesForm d={d} all={all} send={send} />
    </div>
  );
};

// ---------- клавиша выключателя ----------

const SwitchCard: React.FC<{ d: HomeDevice; all: HomeDevice[]; send: (m: object) => void }> = ({ d, all, send }) => {
  const usable = d.online === true;
  const badge = d.online === null ? ['off', 'проверяю…'] : !d.online ? ['alert', d.error ?? 'не отвечает'] : d.on ? ['ok', 'включён'] : ['off', 'выключен'];
  return (
    <div className="feature-card">
      <h3>
        <span className="op-home-title">
          <span className="op-home-dot" style={{ background: d.on ? '#ffd76a' : 'transparent' }} aria-hidden="true" />
          {d.name || d.id}
        </span>
        <span className={`badge ${badge[0]}`}>{badge[1]}</span>
      </h3>
      <div className="row">
        <button type="button" className={`op-btn ${d.on ? '' : 'primary'}`} disabled={!usable}
                onClick={() => send({ cmd: 'light', targets: [d.id], on: !d.on, source: 'site' })}>
          {d.on ? 'Выключить' : 'Включить'}
        </button>
      </div>
      <p className="hint">Выключатель Tuya, клавиша {d.channel ?? 1} · {d.host}{d.seen ? ` · на связи ${timeOf(d.seen)}` : ''}.
        Управляется по сети напрямую (локальный ключ хранится на роботе).</p>
      <NamesForm d={d} all={all} send={send} />
    </div>
  );
};

// ---------- пресеты дома ----------

const DAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];

const SceneParts: React.FC<{ sc: HomeScene; devices: HomeDevice[] }> = ({ sc, devices }) => (
  <small>
    {Object.entries(sc.devices).map(([id, st], i) => {
      const d = devices.find(x => x.id === id);
      const name = d ? (d.name || d.id) : id;
      const lit = st.on && !(d && isSwitch(d));
      return (
        <React.Fragment key={id}>
          {i > 0 && ' · '}
          {name}:{' '}
          {lit && st.rgb && !st.effect && <span className="op-home-dot op-home-dot-sm" style={{ background: toHex(st.rgb) }} aria-hidden="true" />}
          {!st.on ? 'выкл' : !lit ? 'вкл' : `${st.effect ? VOICE_EFFECTS[st.effect] ?? st.effect : ''}${st.level != null ? `${st.effect ? ', ' : ''}${st.level} %` : ''}`}
        </React.Fragment>
      );
    })}
  </small>
);

const ScenesCard: React.FC<{ scenes: HomeScene[]; devices: HomeDevice[]; send: (m: object) => void }> = ({ scenes, devices, send }) => {
  const [name, setName] = useState('');
  const save = () => {
    const n = name.trim();
    if (!n) return;
    const same = scenes.find(sc => sc.name.toLowerCase() === n.toLowerCase());
    if (same && !window.confirm(`Пресет «${same.name}» уже есть. Перезаписать текущим светом?`)) return;
    send({ cmd: 'scene_save', name: n, source: 'site' });
    setName('');
  };
  return (
    <div className="feature-card">
      <h3><span>Пресеты</span></h3>
      {scenes.length === 0 ? <p className="hint">Пресетов пока нет — настройте свет и сохраните его ниже.</p> : (
        <ul className="op-home-list">
          {scenes.map(sc => (
            <li key={sc.id}>
              <div className="op-home-list-main">
                <b>{sc.name}</b>
                <SceneParts sc={sc} devices={devices} />
              </div>
              <div className="row">
                <button type="button" className="btn-sm primary" onClick={() => send({ cmd: 'scene_apply', id: sc.id, source: 'site' })}>Включить</button>
                <button type="button" className="btn-sm" title="Сохранить текущий свет в этот пресет"
                        onClick={() => window.confirm(`Перезаписать «${sc.name}» текущим светом?`) && send({ cmd: 'scene_save', name: sc.name, source: 'site' })}>
                  Перезаписать
                </button>
                <button type="button" className="btn-sm danger"
                        onClick={() => window.confirm(`Удалить пресет «${sc.name}»? Строки расписания с ним тоже удалятся.`) && send({ cmd: 'scene_delete', id: sc.id, source: 'site' })}>
                  Удалить
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="op-trainer-grid">
        <label className="op-trainer-field">
          <span>Сохранить текущий свет как</span>
          <input type="text" value={name} maxLength={40} placeholder="например, кино" onChange={e => setName(e.target.value)}
                 onKeyDown={e => e.key === 'Enter' && save()} />
        </label>
        <button type="button" className="op-btn" disabled={!name.trim()} onClick={save}>Сохранить</button>
      </div>
      <p className="hint">Пресет помнит, что горит сейчас: ленты (цвет, яркость, эффект) и основной свет. Голосом: «Грач, сохрани свет
        как кино», «Грач, включи кино», «Грач, удали пресет кино».</p>
    </div>
  );
};

// ---------- расписание ----------

const ScheduleRow: React.FC<{ e: HomeScheduleEntry; scenes: HomeScene[]; send: (m: object) => void }> = ({ e, scenes, send }) => {
  const [time, setTime] = useState(e.time);
  useEffect(() => setTime(e.time), [e.time]);
  const set = (patch: object) => send({ cmd: 'schedule_set', id: e.id, source: 'site', ...patch });
  const what = e.off ? '__off' : (e.scene ?? '');
  const toggleDay = (d: number) => {
    const days = e.days.includes(d) ? e.days.filter(x => x !== d) : [...e.days, d];
    if (days.length) set({ days });
  };
  return (
    <li className={e.enabled ? '' : 'off'}>
      <div className="op-home-sched">
        <input type="time" value={time} aria-label="Время" onChange={ev => setTime(ev.target.value)}
               onBlur={() => time && time !== e.time && set({ time })} />
        <select value={what} aria-label="Что сделать" onChange={ev => set(ev.target.value === '__off' ? { off: true } : { scene: ev.target.value })}>
          <option value="__off">выключить всё</option>
          {e.scene && !scenes.some(sc => sc.name === e.scene) && <option value={e.scene}>{e.scene} (нет такого)</option>}
          {scenes.map(sc => <option key={sc.id} value={sc.name}>пресет «{sc.name}»</option>)}
        </select>
        <div className="op-chips" role="group" aria-label="Дни недели">
          {DAYS.map((name, d) => (
            <button key={name} type="button" className={`op-chip-btn ${e.days.includes(d) ? 'on' : ''}`} aria-pressed={e.days.includes(d)}
                    onClick={() => toggleDay(d)}>{name}</button>
          ))}
        </div>
        <label className="op-check">
          <input type="checkbox" checked={e.enabled} onChange={ev => set({ enabled: ev.target.checked })} /> вкл
        </label>
        <button type="button" className="btn-sm danger" onClick={() => send({ cmd: 'schedule_delete', id: e.id, source: 'site' })}>Удалить</button>
      </div>
      {e.last && <small className="hint">Последний раз: {e.last}</small>}
    </li>
  );
};

const ScheduleCard: React.FC<{ schedule: HomeScheduleEntry[]; scenes: HomeScene[]; send: (m: object) => void }> = ({ schedule, scenes, send }) => {
  const [time, setTime] = useState('07:00');
  const [what, setWhat] = useState('__off');
  useEffect(() => { if (what === '__off' && scenes.length && !schedule.length) setWhat(scenes[0].name); }, [scenes.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = () => send({ cmd: 'schedule_set', time, source: 'site', ...(what === '__off' ? { off: true } : { scene: what }) });
  return (
    <div className="feature-card">
      <h3><span>Расписание</span></h3>
      {schedule.length === 0 ? <p className="hint">Расписание пустое.</p> : (
        <ul className="op-home-list">{schedule.map(e => <ScheduleRow key={e.id} e={e} scenes={scenes} send={send} />)}</ul>
      )}
      <div className="op-home-sched">
        <input type="time" value={time} aria-label="Время новой строки" onChange={e => setTime(e.target.value)} />
        <select value={what} aria-label="Что сделать" onChange={e => setWhat(e.target.value)}>
          <option value="__off">выключить всё</option>
          {scenes.map(sc => <option key={sc.id} value={sc.name}>пресет «{sc.name}»</option>)}
        </select>
        <button type="button" className="op-btn" disabled={!time} onClick={add}>Добавить</button>
      </div>
      <p className="hint">Каждый день в указанное время робот включает пресет или выключает весь свет — по своим часам, даже когда сайт
        закрыт. Пропущенное время (робот был выключен) не догоняется. Одна строка на пресет: новая с тем же пресетом заменит время.
        Голосом: «Грач, каждый день в семь утра включай утро», «Грач, по будням в 23:00 выключай свет», «Грач, перенеси утро на
        восемь», «Грач, удали расписание утро», «Грач, какое расписание».</p>
    </div>
  );
};

// ---------- список лент ----------

const StripsCard: React.FC<{ devices: HomeDevice[]; transition: number; send: (m: object) => void }> = ({ devices, transition, send }) => {
  const [host, setHost] = useState('');
  const [fade, setFade] = useState(`${transition}`);
  useEffect(() => setFade(`${transition}`), [transition]);
  const add = () => {
    const h = host.trim();
    if (!h) return;
    send({ cmd: 'config', devices: [...devices.map(conf), { host: h, name: '', aliases: [] }] });
    setHost('');
  };
  const commitFade = () => {
    const v = Number(fade);
    if (Number.isFinite(v) && v !== transition) send({ cmd: 'config', transition_s: v });
  };
  return (
    <div className="feature-card">
      <h3><span>Ленты WLED</span></h3>
      <div className="op-trainer-grid">
        <label className="op-trainer-field">
          <span>Добавить ленту: адрес</span>
          <input type="text" value={host} placeholder="wled-3.local или 192.168.0.60" onChange={e => setHost(e.target.value)}
                 onKeyDown={e => e.key === 'Enter' && add()} />
        </label>
        <button type="button" className="op-btn" disabled={!host.trim()} onClick={add}>Добавить</button>
        <label className="op-trainer-field">
          <span>Плавность смены, с</span>
          <input type="number" min={0} max={10} step={0.1} value={fade} onChange={e => setFade(e.target.value)} onBlur={commitFade}
                 onKeyDown={e => e.key === 'Enter' && commitFade()} />
        </label>
        <button type="button" className="btn-sm" onClick={() => send({ cmd: 'refresh' })}>Перечитать ленты</button>
      </div>
      <p className="hint">
        Ленты — контроллеры с прошивкой WLED в той же сети, что и робот; робот говорит с ними сам (JSON API WLED),
        сайт только передаёт команды. Имена <code>.local</code> на Pi работают с avahi-daemon и libnss-mdns; надёжнее
        закрепить за лентами адреса в роутере. Яркость голосом меняется ступенями 2–5–10–20–35–55–75–100 %, чтобы
        каждый шаг был заметен. На WS2812/WS2813 белый — это все три цвета сразу (холодный), тёплый — смесь.
      </p>
    </div>
  );
};

const SmartHomePanel: React.FC = () => {
  const { st, send } = useHome();
  const devices = st?.devices ?? [];
  const strips = devices.filter(d => !isSwitch(d));
  return (
    <>
      <ButlerCard />
      {!st ? (
        <div className="feature-card">
          <h3><span>Ленты</span><span className="badge off">нет связи</span></h3>
          <p className="hint">Нода home на роботе не отвечает (не на связи или старая версия ПО на Pi).</p>
        </div>
      ) : (
        <>
          {devices.length === 0 && <div className="feature-card"><p className="hint">Устройств нет — добавьте ленты ниже.</p></div>}
          {devices.filter(isSwitch).map(d => <SwitchCard key={d.id} d={d} all={devices} send={send} />)}
          {strips.map((d, i) => <StripCard key={d.id} d={d} n={i + 1} all={devices} send={send} />)}
          <ScenesCard scenes={st.scenes ?? []} devices={devices} send={send} />
          <ScheduleCard schedule={st.schedule ?? []} scenes={st.scenes ?? []} send={send} />
          <StripsCard devices={devices} transition={st.config.transition_s} send={send} />
        </>
      )}
    </>
  );
};

export default SmartHomePanel;
